import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../services/authentication/supabaseService';
import { Header } from '../landingpage/sections/Header';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);

  useEffect(() => {
    // Check if we have a valid session from the password reset link
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsValidSession(true);
      } else {
        setError(t('auth.invalidResetLink'));
      }
    });

    // Listen for PASSWORD_RECOVERY event
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    // Validate password strength (minimum 6 characters)
    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: password,
    });

    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      // Password updated successfully, redirect to login
      navigate('/login', {
        state: { message: t('auth.passwordResetSuccess') }
      });
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG text-white">
      <Header />
      <div className="flex justify-center px-5 pt-20">
        <div className="relative z-10 w-full max-w-md h-fit">
          <div className="rounded-2xl border border-white/10 p-8 bg-white/5 backdrop-blur-sm">
            <h1 className="text-2xl font-semibold text-white mb-2">{t('auth.resetPassword')}</h1>
            <p className="text-sm text-white/70 mb-6">{t('auth.resetPasswordDescription')}</p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                {error}
              </div>
            )}

            {isValidSession ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="password"
                    placeholder={t('auth.newPassword')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="password"
                    placeholder={t('auth.confirmNewPassword')}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 text-base font-semibold bg-accent text-white hover:bg-accent/90 rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.updatingPassword') : t('auth.updatePassword')}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <p className="text-white/70 mb-4">{t('auth.invalidOrExpiredLink')}</p>
                <a
                  href="/forgot-password"
                  className="text-accent hover:underline font-medium"
                >
                  {t('auth.requestNewResetLink')}
                </a>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-white/70">
              {t('auth.rememberPassword')}{' '}
              <a href="/login" className="text-white font-medium hover:underline">
                {t('auth.backToLogin')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
